import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  CButton,
  CCard,
  CCardBody,
  CCol,
  CContainer,
  CRow,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
  CAlert,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilEnvelopeClosed, cilEnvelopeOpen } from '@coreui/icons'
import apiClient from '../../services/apiClient'
import { useNavigate } from 'react-router-dom'
import { buildApiUrl } from '../../utils/getConfiguration'
import { SplashScreen } from '@capacitor/splash-screen'
import EmptyStatePanel from '../../components/EmptyStatePanel'
import InternalAppHeader from './components/InternalAppHeader'
import { useTranslation } from 'react-i18next'

const hideSplashScreen = () => {
  if (typeof window === 'undefined') {
    return
  }

  const splash = document.getElementById('splash-screen')
  if (splash) splash.style.display = 'none'

  if (typeof SplashScreen?.hide === 'function') {
    SplashScreen.hide({ fadeOutDuration: 200 }).catch((error) => {
      console.warn('Unable to hide splash screen in Inbox', error)
    })
  } else if (typeof window.SplashScreen?.hide === 'function') {
    window.SplashScreen.hide({ fadeOutDuration: 200 }).catch((error) => {
      console.warn('Unable to hide splash screen in Inbox', error)
    })
  }
}

const Inbox = () => {
  const [messages, setMessages] = useState([])
  const [selectedMessageId, setSelectedMessageId] = useState(null)
  const [feedback, setFeedback] = useState(null)
  const { t } = useTranslation()

  const fetchMessagesData = useCallback(async () => {
    const account_ID = localStorage.getItem('account_ID')
    if (!account_ID) {
      setMessages([])
      setSelectedMessageId(null)
      return
    }

    try {
      const response = await apiClient.get(buildApiUrl('messages', { account_ID }))
      const payload = Array.isArray(response.data) ? response.data : []
      const normalizedMessages = payload
        .map((message) => {
          if (!message) {
            return null
          }
          if (message.message_id !== undefined && message.message_id !== null) {
            return message
          }
          const fallbackId =
            message.message_ID ?? message.id ?? message.ID ?? message.msg_id ?? message.Msg_ID
          if (fallbackId === undefined || fallbackId === null) {
            return null
          }
          return { ...message, message_id: fallbackId }
        })
        .filter(Boolean)
      const sortedMessages = [...normalizedMessages].sort((a, b) => {
        const aId = Number(a.message_id) || 0
        const bId = Number(b.message_id) || 0
        return bId - aId
      })
      setMessages(sortedMessages)

      if (sortedMessages.length === 0) {
        setSelectedMessageId(null)
      } else {
        const hasSelection = sortedMessages.some(
          (message) => String(message.message_id) === selectedMessageId,
        )
        if (!hasSelection) {
          setSelectedMessageId(String(sortedMessages[0].message_id))
        }
      }
    } catch (error) {
      console.error('Error fetching messages', error)
      setFeedback({ color: 'danger', message: t('inbox.feedback.loadError') })
    }
  }, [selectedMessageId, t])

  useEffect(() => {
    fetchMessagesData().finally(() => {
      hideSplashScreen()
    })
  }, [fetchMessagesData])

  const markMessageAsRead = useCallback(
    async (messageId) => {
      const account_ID = localStorage.getItem('account_ID')
      if (!account_ID) {
        return
      }

      await apiClient.get(
        buildApiUrl('messageUpdate', {
          message_status: 'N',
          account_ID: Number(account_ID),
          message_ID: Number(messageId),
        }),
      )
      await fetchMessagesData()
    },
    [fetchMessagesData],
  )

  const handleSelectMessage = useCallback(
    async (message) => {
      const id = String(message.message_id)
      localStorage.setItem('message_id', id)
      setSelectedMessageId(id)

      if (String(message.message_status) === 'Y') {
        await markMessageAsRead(id)
      }
    },
    [markMessageAsRead],
  )

  const selectedMessage = useMemo(() => {
    if (!selectedMessageId) {
      return null
    }
    return messages.find((message) => String(message.message_id) === selectedMessageId) ?? null
  }, [messages, selectedMessageId])

  const navigate = useNavigate()

  return (
    <div className="buying-list-wrapper min-vh-100 d-flex flex-column">
      <CContainer className="py-4 flex-grow-1">
        <InternalAppHeader title={t('inbox.header.title')} onBack={() => navigate('/dashboard')} />

        <CRow className="justify-content-center g-4 mt-2">
          <CCol lg={10} className="d-flex flex-column gap-4">
            {feedback && (
              <CAlert
                color={feedback.color || 'danger'}
                dismissible
                className="mb-0"
                onClose={() => setFeedback(null)}
              >
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <span>{feedback.message}</span>
                  <CButton
                    color={feedback.color || 'danger'}
                    variant="outline"
                    size="sm"
                    onClick={fetchMessagesData}
                  >
                    {t('inbox.actions.retry')}
                  </CButton>
                </div>
              </CAlert>
            )}

            <CRow className="g-4">
              <CCol lg={5} className="small text-medium-emphasis">
                {selectedMessage ? (
                  <CCard key={selectedMessage.message_id} className="border-0 shadow-sm h-100">
                    <CCardBody className="d-flex flex-column gap-3">
                      <div>
                        <h5 className="mb-1">{selectedMessage.subject}</h5>
                        <div className="d-flex flex-wrap justify-content-between text-muted small">
                          <span>{selectedMessage.message_from}</span>
                          <span>{selectedMessage.message_date}</span>
                        </div>
                      </div>
                      <pre className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                        {selectedMessage.message_content}
                      </pre>
                    </CCardBody>
                  </CCard>
                ) : (
                  <EmptyStatePanel
                    icon={cilEnvelopeClosed}
                    title={t('inbox.empty.detailTitle')}
                    message={t('inbox.empty.detailMessage')}
                  />
                )}
              </CCol>
              <CCol lg={7}>
                <CCard className="border-0 shadow-sm h-100">
                  <CCardBody className="p-0">
                    <CTable align="middle" className="mb-0" hover responsive>
                      <CTableHead color="light">
                        <CTableRow>
                          <CTableHeaderCell className="text-center">
                            {t('inbox.table.headers.status')}
                          </CTableHeaderCell>
                          <CTableHeaderCell>{t('inbox.table.headers.message')}</CTableHeaderCell>
                          <CTableHeaderCell className="text-center">
                            {t('inbox.table.headers.from')}
                          </CTableHeaderCell>
                          <CTableHeaderCell className="text-center">
                            {t('inbox.table.headers.date')}
                          </CTableHeaderCell>
                        </CTableRow>
                      </CTableHead>
                      <CTableBody>
                        {messages.length > 0 ? (
                          messages.map((message) => {
                            const isSelected = String(message.message_id) === selectedMessageId
                            const isUnread = String(message.message_status) === 'Y'
                            const icon = isUnread ? cilEnvelopeClosed : cilEnvelopeOpen
                            const rowClass = isSelected ? 'table-active' : ''

                            const handleRowClick = () => {
                              handleSelectMessage(message)
                            }

                            return (
                              <CTableRow
                                key={message.message_id}
                                className={`align-middle ${rowClass}`}
                              >
                                <CTableDataCell className="text-center">
                                  <CButton
                                    size="sm"
                                    color="link"
                                    className="p-0 text-decoration-none"
                                    onClick={handleRowClick}
                                    aria-label={
                                      isUnread
                                        ? t('inbox.table.aria.unread')
                                        : t('inbox.table.aria.read')
                                    }
                                  >
                                    <CIcon
                                      icon={icon}
                                      size="sm"
                                      className={isUnread ? 'text-info' : 'text-muted'}
                                    />
                                  </CButton>
                                </CTableDataCell>
                                <CTableDataCell role="button" onClick={handleRowClick}>
                                  <div className="fw-semibold">{message.subject}</div>
                                </CTableDataCell>
                                <CTableDataCell
                                  className="text-center"
                                  role="button"
                                  onClick={handleRowClick}
                                >
                                  <div className="small text-medium-emphasis text-nowrap">
                                    {message.message_from}
                                  </div>
                                </CTableDataCell>
                                <CTableDataCell
                                  className="text-center"
                                  role="button"
                                  onClick={handleRowClick}
                                >
                                  <div className="small text-medium-emphasis text-nowrap">
                                    {message.message_date}
                                  </div>
                                </CTableDataCell>
                              </CTableRow>
                            )
                          })
                        ) : (
                          <CTableRow>
                            <CTableDataCell colSpan={4}>
                              <EmptyStatePanel
                                icon={cilEnvelopeClosed}
                                title={t('inbox.empty.tableTitle')}
                                message={t('inbox.empty.tableMessage')}
                              />
                            </CTableDataCell>
                          </CTableRow>
                        )}
                      </CTableBody>
                    </CTable>
                  </CCardBody>
                </CCard>
              </CCol>
            </CRow>
          </CCol>
        </CRow>
      </CContainer>
    </div>
  )
}

export default Inbox
